import { type ButtonHTMLAttributes, memo, type ReactNode } from 'react'
import { clsx } from 'clsx'

import css from './Button.module.scss'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'gradient' | 'danger'
  children: ReactNode
  className?: string
  isStatic?: boolean
  /**
   * Shows an inline spinner, disables the button, and marks it aria-busy.
   * Keeps the label available rather than swapping the action text.
   */
  isLoading?: boolean
}

function Button({
  variant = 'primary',
  children,
  className,
  isStatic,
  isLoading = false,
  disabled,
  // Default to "button" — an unspecified <button> submits any form it sits
  // inside, which is almost never what a call site means.
  type = 'button',
  ...props
}: ButtonProps) {
  // `||`, not `??`: an explicit disabled={false} must not re-enable a button
  // that is mid-request, or a second click submits twice.
  const isDisabled = Boolean(disabled) || isLoading
  const isVisuallyDisabled = Boolean(disabled) && !isLoading

  const button = (
    <button
      type={type}
      className={clsx(
        'Button',
        css.root,
        css[variant],
        isStatic && css.isStatic,
        isLoading && css.isLoading,
        className
      )}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading && <span className={css.spinner} aria-hidden="true" />}
      <span className={css.label}>{children}</span>
    </button>
  )

  if (variant === 'gradient') {
    return <span className={clsx(css.gradientWrapper, isVisuallyDisabled && css.isDisabled)}>{button}</span>
  }

  return button
}

export default memo(Button)
